import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { paymentAPI } from '../services/api';
import './PaymentPage.css';

interface TickPackage {
  ticks: number;
  price: number;
  description: string;
  discount: string;
  reports: number;
}

interface PaymentInfo {
  tick_price: number;
  report_cost: number;
  available_reports: number;
  balance: number;
  tick_packages: TickPackage[];
}

const PaymentPage: React.FC = () => {
  const { t } = useTranslation('profile');
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [customTicks, setCustomTicks] = useState<number>(0);

  // Check if we're on the success page
  const isSuccessPage = location.pathname === '/payment/success';
  const searchParams = new URLSearchParams(location.search);
  const paymentId = searchParams.get('payment_id');

  // Define checkPaymentStatus as a useCallback to use it in multiple places
  const checkPaymentStatus = useCallback(async (id: string) => {
    console.log(`Checking payment status for ID: ${id}`);
    try {
      setIsLoading(true);
      const response = await paymentAPI.checkPaymentStatus(id);
      console.log('Payment status response:', response.data);
      
      // Update payment info
      setPaymentInfo(prev => ({
        ...prev!,
        balance: response.data.balance,
        available_reports: response.data.reports_available
      }));
      
      // If payment is successful, refresh user data to update balance in context
      if (response.data.status === 'succeeded') {
        console.log('Payment succeeded, refreshing user data');
        await refreshUser();
      }
      
      setIsLoading(false);
      return response.data.status;
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError(t('payment.error_status'));
      setIsLoading(false);
      return null;
    }
  }, [refreshUser]);

  // Get last pending payment if no payment ID in URL
  const getLastPendingPayment = useCallback(async () => {
    console.log('Getting last pending payment');
    try {
      const response = await paymentAPI.getLastPendingPayment();
      console.log('Last pending payment response:', response.data);
      if (response.data.payment_id) {
        return response.data.payment_id;
      }
      return null;
    } catch (err) {
      console.error('Error getting last pending payment:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    const fetchPaymentInfo = async () => {
      try {
        setIsLoading(true);
        const response = await paymentAPI.getPaymentInfo();
        setPaymentInfo(response.data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching payment info:', err);
        setError(t('payment.error_loading'));
        setIsLoading(false);
      }
    };

    fetchPaymentInfo();
    
    // Handle payment status checking
    if (isSuccessPage) {
      const processPayment = async () => {
        // First try with payment ID from URL
        if (paymentId) {
          console.log(`Payment ID found in URL: ${paymentId}`);
          await checkPaymentStatus(paymentId);
        } else {
          // If no payment ID in URL, try to get last pending payment
          console.log('No payment ID in URL, trying to get last pending payment');
          const lastPaymentId = await getLastPendingPayment();
          if (lastPaymentId) {
            console.log(`Found last pending payment: ${lastPaymentId}`);
            await checkPaymentStatus(lastPaymentId);
          } else {
            console.log('No pending payments found');
          }
        }
      };
      
      processPayment();
      
      // Set up periodic checking for pending payments
      const intervalId = setInterval(async () => {
        const id = paymentId || await getLastPendingPayment();
        if (id) {
          const status = await checkPaymentStatus(id);
          if (status === 'succeeded') {
            clearInterval(intervalId);
          }
        } else {
          clearInterval(intervalId);
        }
      }, 5000); // Check every 5 seconds
      
      // Clean up interval on unmount
      return () => clearInterval(intervalId);
    }
  }, [isSuccessPage, paymentId, checkPaymentStatus, getLastPendingPayment]);

  const handleBuyPackage = async (ticks: number) => {
    try {
      setIsProcessing(true);
      const response = await paymentAPI.createPayment(ticks);
      console.log('Payment created:', response.data);
      
      // Store payment ID in localStorage before redirecting
      if (response.data.payment_id) {
        console.log(`Storing payment ID in localStorage: ${response.data.payment_id}`);
        localStorage.setItem('last_payment_id', response.data.payment_id);
      }
      
      // Redirect to payment page
      window.location.href = response.data.payment_url;
    } catch (err: any) {
      console.error('Error creating payment:', err);
      setError(err.response?.data?.detail || t('payment.error_payment'));
      setIsProcessing(false);
    }
  };

  const handleCustomTicksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0) {
      setCustomTicks(value);
    }
  };

  const calculateCustomPrice = () => {
    if (!paymentInfo || customTicks <= 0) return 0;
    
    const baseAmount = customTicks * paymentInfo.tick_price;
    
    // Apply discounts based on number of ticks
    if (customTicks >= 5000) {
      return baseAmount * 0.75; // 25% discount
    } else if (customTicks >= 1000) {
      return baseAmount * 0.85; // 15% discount
    } else if (customTicks >= 500) {
      return baseAmount * 0.9; // 10% discount
    }
    
    return baseAmount;
  };

  const getDiscountText = () => {
    if (customTicks >= 5000) {
      return t('payment.discount_label', { value: '25%' });
    } else if (customTicks >= 1000) {
      return t('payment.discount_label', { value: '15%' });
    } else if (customTicks >= 500) {
      return t('payment.discount_label', { value: '10%' });
    }
    return '';
  };

  if (isLoading) {
    return (
      <div className="main-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('payment.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-container">
        <div className="error-container">
          <h2>{t('payment.error_loading')}</h2>
          <p>{error}</p>
          <button
            className="back-button"
            onClick={() => navigate('/ticks')}
          >
            {t('payment.back_to_ticks')}
          </button>
        </div>
      </div>
    );
  }

  if (isSuccessPage) {
    return (
      <div className="main-container">
        <div className="payment-success-container">
          <div className="status-icon success">
            <i className="fas fa-check-circle"></i>
          </div>
          <h1 className="status-title">{t('payment.success_title')}</h1>
          <p className="status-message">
            {t('payment.success_text')}
          </p>

          {/* Display current balance */}
          <div className="success-balance">
            <h3>{t('payment.success_balance_title')}</h3>
            <div className="balance-amount">{t('payment.success_balance_value', { count: user?.balance || paymentInfo?.balance || 0 })}</div>
          </div>

          <button
            className="back-button"
            onClick={() => navigate('/ticks')}
          >
            {t('payment.back_to_ticks')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      {/* Текущий баланс */}
      <section className="balance-section">
        <h2 className="balance-title">{t('payment.current_balance_title')}</h2>
        <div className="current-balance">
          {t('payment.balance_value', { count: paymentInfo?.balance || 0 })}
        </div>
        <div className="available-reports" dangerouslySetInnerHTML={{ __html: t('payment.available_reports', { count: paymentInfo?.available_reports || 0 }) }} />
      </section>

      {/* Пакеты тиков */}
      <div className="packages-grid">
        {paymentInfo?.tick_packages.map((pkg, index) => (
          <div 
            key={index} 
            className={`package-card ${pkg.ticks === 1000 ? 'popular' : ''}`}
          >
            {pkg.ticks === 1000 && (
              <div className="popular-badge">{t('payment.popular_badge')}</div>
            )}

            <div className="package-ticks">{pkg.ticks} {t('payment.ticks_unit')}</div>
            <div className="package-reports">
              {pkg.reports} {t('payment.reports_unit')}
            </div>
            <div className="package-description">{pkg.description}</div>
            
            {pkg.discount && (
              <div className="package-discount">
                {t('payment.discount_label', { value: pkg.discount })}
              </div>
            )}
            
            <div className="package-price">{pkg.price} ₽</div>
            
            <button 
              className="buy-button" 
              onClick={() => handleBuyPackage(pkg.ticks)}
              disabled={isProcessing}
            >
              <i className="fas fa-wallet"></i>
              {isProcessing ? t('payment.processing') : t('payment.buy_button')}
            </button>
          </div>
        ))}

        {/* Custom package */}
        <div className="package-card custom-package">
          <div className="package-ticks">{t('payment.custom_label')}</div>
          <div className="custom-ticks-input">
            <input
              type="number"
              min="1"
              value={customTicks || ''}
              onChange={handleCustomTicksChange}
              placeholder={t('payment.custom_placeholder')}
            />
          </div>
          
          {customTicks > 0 && (
            <>
              <div className="package-reports">
                {Math.floor(customTicks / (paymentInfo?.report_cost || 100))} {t('payment.reports_unit')}
              </div>
              
              {getDiscountText() && (
                <div className="package-discount">
                  {getDiscountText()}
                </div>
              )}
              
              <div className="package-price">{calculateCustomPrice()} ₽</div>
            </>
          )}
          
          <button 
            className="buy-button" 
            onClick={() => handleBuyPackage(customTicks)}
            disabled={isProcessing || customTicks <= 0}
          >
            <i className="fas fa-wallet"></i>
            {isProcessing ? t('payment.processing') : t('payment.buy_button')}
          </button>
        </div>
      </div>

      {/* Price info */}
      <div className="price-info">
        <p>{t('payment.price_info_base', { price: paymentInfo?.tick_price })}</p>
        <p>{t('payment.price_info_cost', { cost: paymentInfo?.report_cost })}</p>
        <p>{t('payment.price_info_discount')}</p>
      </div>

      {/* Оверлей загрузки */}
      {isProcessing && (
        <div className="loading-overlay" style={{ display: 'flex' }}>
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;
